import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import SecurityChecksTable from './SecurityChecksTable';
import { PostureFreshnessPill } from './GoogleWorkspaceDetail';
import Header from './Header';
import {
  ArrowLeft, ShieldCheck, RefreshCw, ExternalLink, AlertCircle, CheckCircle,
  Loader2, Users as UsersIcon, FileText, Settings as SettingsIcon, Shield,
  Building2, KeyRound, ChevronDown, ChevronRight, Play, XCircle, Info, Download, X, Save, Clock,
} from 'lucide-react';
import { formatDateTime, parseServerDate } from '../utils/dateFormatter';

const SEV_TONE = {
  critical: 'text-red-700 bg-red-50 border-red-200',
  high: 'text-orange-700 bg-orange-50 border-orange-200',
  medium: 'text-amber-700 bg-amber-50 border-amber-200',
  low: 'text-slate-600 bg-slate-50 border-slate-200',
  informational: 'text-slate-600 bg-slate-50 border-slate-200',
};

// "informational" is presented as Low per product decision; ranking sorts
// most-severe first (Critical → High → Medium → Low).
const SEV_LABEL = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low', informational: 'Low' };
const SEV_RANK = { critical: 0, high: 1, medium: 2, low: 3, informational: 3 };
// Points weighted by severity (Critical +5, High +3, Medium +2, Low +1).
const SEV_POINTS = { critical: 5, high: 3, medium: 2, low: 1, informational: 1 };
const sevLabel = (s) => SEV_LABEL[s] || 'Low';
const sevRank = (s) => (SEV_RANK[s] ?? 3);
const sevPoints = (s) => (SEV_POINTS[s] ?? 1);

// Short Entra/admin-center location paths for the "Fix" column — derived
// from each finding's deep_link so the column shows a breadcrumb (like the
// Google Workspace table) instead of a long how-to-fix sentence. Display-only.
const FIX_LOCATIONS = [
  ['SecurityDefaultsBlade', 'Entra → Identity → Overview → Security defaults'],
  ['AuthenticationMethodsMenuBlade', 'Entra → Protection → Authentication methods'],
  ['RolesManagementMenuBlade', 'Entra → Identity → Roles & admins'],
  ['UserManagementMenuBlade', 'Entra → Identity → Users'],
  ['UserSettingsMenuBlade', 'Entra → Identity → Users → User settings'],
  ['CompanyRelationshipsMenuBlade', 'Entra → External Identities → Collaboration settings'],
  ['CrossTenantAccessSettings', 'Entra → External Identities → Cross-tenant access'],
  ['ConditionalAccessBlade', 'Entra → Protection → Conditional Access'],
  ['ConsentPoliciesMenuBlade', 'Entra → Enterprise apps → Consent & permissions'],
  ['PasswordProtectionBlade', 'Entra → Protection → Password protection'],
  ['PasswordResetMenuBlade', 'Entra → Protection → Password reset'],
  ['IdentityProtectionMenuBlade', 'Entra → Protection → Identity Protection'],
  ['ApplicationsListBlade', 'Entra → App registrations'],
  ['Microsoft_Azure_PIMCommon', 'Entra → Identity Governance → PIM'],
  ['Microsoft_AAD_ERM', 'Entra → Identity Governance → Access reviews'],
  ['admin.microsoft.com/sharepoint', 'SharePoint admin → Policies → Sharing'],
  ['purview.microsoft.com/informationprotection', 'Purview → Information protection → Labels'],
  ['purview.microsoft.com/datalossprevention', 'Purview → Data loss prevention'],
  ['purview.microsoft.com/audit', 'Purview → Audit'],
  ['intune.microsoft.com', 'Intune → Devices → Compliance'],
  ['security.microsoft.com/safeattachment', 'Defender → Policies → Safe Attachments'],
  ['security.microsoft.com/safelinks', 'Defender → Policies → Safe Links'],
  ['security.microsoft.com/antiphishing', 'Defender → Email & collaboration → Anti-phishing'],
  ['security.microsoft.com/antispam', 'Defender → Email & collaboration → Anti-spam'],
  ['security.microsoft.com', 'Defender → Email & collaboration'],
  ['compliance.microsoft.com', 'Purview → Compliance'],
  ['admin.exchange.microsoft.com', 'Exchange admin → Settings'],
];
const m365FixLocation = (f) => {
  const link = f?.deep_link || '';
  const hit = FIX_LOCATIONS.find(([key]) => link.includes(key));
  return hit ? hit[1] : null;
};

// For "Not assessed" checks: derive what's needed to assess the check from the
// requirement stored in the finding's proof ("What we found"). Display-only.
const m365NaRequirement = (f) => {
  const text = f?.proof || '';
  if (/Exchange Online|Microsoft Purview/i.test(text)) {
    return {
      fix: 'Connect Exchange Online',
      how: 'Connect the Exchange Online / Microsoft Purview integration, then Re-evaluate.',
    };
  }
  const perms = text.match(/[A-Za-z]+\.Read\.All/g) || [];
  if (perms.length) {
    const lic = (text.match(/Entra ID P[12]/) || [])[0];
    return {
      fix: 'Grant permission + Re-evaluate',
      how: `Grant ${perms.join(' + ')} admin consent in Entra${lic ? ` (and ${lic})` : ''}, then click Re-evaluate.`,
    };
  }
  // Fallback (e.g. a setting not exposed by read-only Graph).
  return {
    fix: 'Check in Entra + Re-evaluate',
    how: `${text}${text ? ' — ' : ''}then click Re-evaluate.`,
  };
};
const m365NaFix = (f) => m365NaRequirement(f).fix;
const m365NaHowToFix = (f) => m365NaRequirement(f).how;

// Friendly names for the permission/license-gated Graph sources we may skip.
const SKIPPED_LABELS = {
  users_signin_activity: 'Sign-in activity (stale account detection)',
  registration_details: 'MFA registration report',
  risky_users: 'Identity Protection — risky users',
  sharepoint_settings: 'SharePoint / OneDrive sharing settings',
  role_eligibility: 'Privileged Identity Management (PIM)',
  applications: 'App registration credentials',
  sensitivity_labels: 'Sensitivity labels',
  managed_devices: 'Intune managed devices',
  device_compliance_policies: 'Intune device compliance policies',
};

const scoreTone = (s) => (s == null ? 'text-slate-400' : s >= 85 ? 'text-emerald-600' : s >= 70 ? 'text-amber-600' : 'text-red-600');

// Always turn an axios error into a plain string — FastAPI 422 responses put an
// array of {type, loc, msg, input, url} objects in `detail`, and rendering that
// object as a toast/React child crashes the page. Never return an object here.
const errMsg = (e, fallback = 'Something went wrong') => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => (x && x.msg) || (typeof x === 'string' ? x : '')).filter(Boolean).join(', ') || fallback;
  if (d && typeof d === 'object') return d.msg || fallback;
  return e?.message || fallback;
};

const TABS = [
  { id: 'overview', label: 'Overview', icon: ShieldCheck },
  { id: 'checks', label: 'Security Checks', icon: Shield },
  { id: 'directory', label: 'Directory', icon: UsersIcon },
  { id: 'evidence', label: 'Audit Evidence', icon: FileText },
  { id: 'settings', label: 'Settings', icon: SettingsIcon },
];

const Microsoft365Detail = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';
  const setTab = (id) => setSearchParams((p) => { p.set('tab', id); return p; });

  const currentWorkspace = useMemo(
    () => JSON.parse(localStorage.getItem('current_workspace') || 'null'), []);
  const workspaceId = currentWorkspace?.id;
  const user = useMemo(() => JSON.parse(localStorage.getItem('user') || 'null'), []);

  const [connection, setConnection] = useState(null);
  const [posture, setPosture] = useState(null);
  const [directory, setDirectory] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collecting, setCollecting] = useState(false);

  const loadSettings = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const r = await axios.get('/api/workspace-settings/microsoft-365', { params: { workspace_id: workspaceId } });
      setSettings(r.data);
    } catch { /* noop */ }
  }, [workspaceId]);

  const loadConnection = useCallback(async () => {
    if (!user?.id || !workspaceId) return null;
    const resp = await axios.get(`/api/technologies/connections/${user.id}/${workspaceId}`);
    const m365 = (resp.data?.connections || []).find((c) => c.technology === 'microsoft_365');
    setConnection(m365 || null);
    return m365 || null;
  }, [user, workspaceId]);

  const loadPosture = useCallback(async () => {
    if (!workspaceId) return;
    try {
      // Shared posture engine (same endpoints family as Google Workspace).
      const r = await axios.get(`/api/posture/microsoft-365`, { params: { workspace_id: workspaceId } });
      setPosture(r.data);
    } catch { /* noop */ }
  }, [workspaceId]);

  const loadDirectory = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const r = await axios.get(`/api/technologies/microsoft-365/directory/${workspaceId}`);
      setDirectory(r.data);
    } catch { /* noop */ }
  }, [workspaceId]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await loadConnection();
        await Promise.all([loadPosture(), loadDirectory(), loadSettings()]);
      } finally { setLoading(false); }
    })();
  }, [loadConnection, loadPosture, loadDirectory, loadSettings]);

  const pollJob = useCallback(async (jobId) => {
    for (let i = 0; i < 90; i++) {
      await new Promise((res) => setTimeout(res, 3000));
      try {
        const r = await axios.get(`/api/technologies/evidence-jobs/${jobId}`);
        const st = r.data?.status;
        if (st === 'completed') { toast.success('Microsoft 365 collection completed'); return true; }
        if (st === 'failed') { toast.error(r.data?.error || 'Collection failed'); return false; }
      } catch { /* keep polling */ }
    }
    toast.message('Collection still running — refresh in a moment');
    return false;
  }, []);

  const runCollect = useCallback(async () => {
    if (!user?.id || !workspaceId) return;
    setCollecting(true);
    try {
      // Send the SAME required fields the working Google Workspace collect
      // sends — the endpoint requires customer_name (422 without it).
      const r = await axios.post('/api/technologies/collect-evidence/microsoft-365', {
        user_id: user.id,
        workspace_id: workspaceId,
        customer_name: currentWorkspace?.name || 'Customer',
        customer_email: user?.email,
        customer_organization: currentWorkspace?.name || 'Organization',
        contact_person: user?.email,
      });
      toast.success(r.data?.message || 'Collection started');
      await pollJob(r.data?.job_id);
      await Promise.all([loadPosture(), loadDirectory(), loadConnection()]);
    } catch (e) {
      toast.error(errMsg(e, 'Failed to start collection'));
    } finally { setCollecting(false); }
  }, [user, workspaceId, currentWorkspace, pollJob, loadPosture, loadDirectory, loadConnection]);

  const isActive = connection?.status === 'active';

  // Security Checks "Re-evaluate" — re-runs the M365 checks via the shared
  // posture evaluate job (same pattern as Google Workspace). Checks-only;
  // it does not re-collect evidence.
  const [evalStatus, setEvalStatus] = useState(null); // 'queued'|'running'|'succeeded'|'failed'
  const [evalJobId, setEvalJobId] = useState(null);
  const evalPollRef = React.useRef(null);

  const triggerEvaluation = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const resp = await axios.post('/api/posture/microsoft-365/evaluate', null,
        { params: { workspace_id: workspaceId } });
      const jobId = resp.data?.job_id;
      if (!jobId) { toast.error('Could not start evaluation'); return; }
      setEvalJobId(jobId);
      setEvalStatus(resp.data?.status || 'queued');
      toast.success(resp.data?.deduped
        ? 'Evaluation already in progress — tracking it now'
        : 'Security checks re-evaluation queued');
    } catch (e) {
      toast.error(errMsg(e, 'Could not start evaluation'));
    }
  }, [workspaceId]);

  useEffect(() => {
    if (!evalJobId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await axios.get(`/api/posture/microsoft-365/evaluate-jobs/${evalJobId}`);
        if (cancelled) return;
        const status = r.data?.status;
        setEvalStatus(status);
        if (status === 'succeeded') {
          await loadPosture();
          setEvalJobId(null);
          toast.success('Security checks re-evaluated');
        } else if (status === 'failed' || status === 'dead') {
          toast.error(`Evaluation failed: ${r.data?.error || 'unknown error'}`);
          setEvalJobId(null);
        }
      } catch { /* keep polling */ }
    };
    evalPollRef.current = setInterval(tick, 3000);
    tick();
    return () => {
      cancelled = true;
      if (evalPollRef.current) clearInterval(evalPollRef.current);
    };
  }, [evalJobId, loadPosture]);
  const isEvaluating = evalStatus === 'queued' || evalStatus === 'running';

  // One-click auto-collect: when we arrive here right after a successful
  // Connect & Verify (?autocollect=1), kick off the same full collection +
  // posture pipeline the manual "Collect Evidence" button uses. Guarded so it
  // fires only once and a page refresh doesn't re-trigger it.
  const autoCollectRef = React.useRef(false);
  useEffect(() => {
    if (loading || autoCollectRef.current) return;
    if (searchParams.get('autocollect') !== '1') return;
    if (connection?.status !== 'active') return;
    autoCollectRef.current = true;
    setSearchParams((p) => { p.delete('autocollect'); return p; }, { replace: true });
    runCollect();
  }, [loading, connection, searchParams, setSearchParams, runCollect]);

  if (loading) {
    return (
      <div className="min-h-screen bg-appbg">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 flex items-center justify-center" data-testid="m365-loading">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-appbg">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="m365-detail-page">
        {/* Back Button — matches Google Workspace detail page */}
        <Button
          onClick={() => navigate('/technologies')}
          variant="outline"
          className="mb-6"
          data-testid="m365-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Technologies
        </Button>

        {/* Page title — same shape as Google Workspace */}
        <div className="mb-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-[#0f6cbd]" strokeWidth={1.8} />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-900" data-testid="m365-title">Microsoft 365</h1>
                <p className="text-slate-600">
                  {connection?.connection_params?.tenant_name
                    ? `Microsoft Entra ID · ${connection.connection_params.tenant_name}`
                    : 'Your tenant health, security checks and audit evidence — at a glance.'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {isActive && (
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 inline-flex items-center gap-1" data-testid="m365-status-badge">
                  <CheckCircle className="w-3 h-3" />
                  Connected
                </Badge>
              )}
            </div>
          </div>
        </div>

        {!isActive
          ? <ConnectPanel user={user} workspaceId={workspaceId} connection={connection}
              onConnected={async () => {
                const c = await loadConnection();
                await Promise.all([loadPosture(), loadDirectory()]);
                // Auto-collect immediately after a successful in-page connect.
                if (c?.status === 'active') runCollect();
              }} />
          : (
            <>
              <div className="border-b border-slate-200 mb-6 flex items-center gap-1 overflow-x-auto" data-testid="m365-tab-strip">
                {TABS.map((t) => {
                  const Icon = t.icon;
                  const on = activeTab === t.id;
                  return (
                    <button key={t.id} type="button" onClick={() => setTab(t.id)}
                      data-testid={`m365-tab-${t.id}`}
                      className={`inline-flex items-center gap-2 px-3 py-2 text-sm border-b-2 transition-colors -mb-px whitespace-nowrap ${on ? 'border-slate-900 text-slate-900 font-medium' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
                      <Icon className="w-4 h-4" strokeWidth={1.7} /> {t.label}
                    </button>
                  );
                })}
              </div>

              {activeTab === 'overview' && <OverviewTab posture={posture} onCollect={runCollect} collecting={collecting} onTab={setTab} />}
              {activeTab === 'checks' && <ChecksTab posture={posture} onEvaluate={triggerEvaluation} isEvaluating={isEvaluating} />}
              {activeTab === 'directory' && <DirectoryTab directory={directory} settings={settings} />}
              {activeTab === 'evidence' && <EvidenceLogTab workspaceId={workspaceId} />}
              {activeTab === 'settings' && <SettingsTab connection={connection} user={user} workspaceId={workspaceId} onChanged={loadConnection} onSettingsSaved={loadSettings} onDisconnected={() => navigate('/technologies')} />}
            </>
          )}
      </div>
    </div>
  );
};

/* ---------------- Connect panel ---------------- */
const ConnectPanel = ({ user, workspaceId, connection, onConnected }) => {
  const [info, setInfo] = useState(null);
  const [showSteps, setShowSteps] = useState(true);
  const [form, setForm] = useState({ tenant_id: '', client_id: '', client_secret: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get('/api/technologies/microsoft-365/setup-info').then((r) => setInfo(r.data)).catch(() => {});
  }, []);

  const connect = async () => {
    if (!form.tenant_id || !form.client_id || !form.client_secret) return toast.error('All three fields are required');
    setBusy(true);
    try {
      const r = await axios.post('/api/technologies/connect/microsoft-365', {
        user_id: user.id, workspace_id: workspaceId, verify_now: true, ...form,
      });
      if (r.data?.status === 'active') { toast.success(`Connected to ${r.data.tenant_name || 'Microsoft 365'}`); onConnected(); }
      else toast.error(r.data?.last_verification?.error_msg || 'Connected but verification failed — check permissions & admin consent');
    } catch (e) {
      toast.error(errMsg(e, 'Connection failed'));
    } finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="m365-connect-panel">
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><KeyRound className="w-4 h-4" /> Connect your tenant</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {connection?.last_verification && !connection.last_verification.success && (
            <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="m365-verify-error">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Last verification failed: {connection.last_verification.error_msg || connection.last_verification.code}</span>
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-slate-600">Directory (Tenant) ID</label>
            <Input value={form.tenant_id} onChange={(e) => setForm({ ...form, tenant_id: e.target.value.trim() })}
              placeholder="00000000-0000-0000-0000-000000000000" data-testid="m365-tenant-id-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Application (Client) ID</label>
            <Input value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value.trim() })}
              placeholder="00000000-0000-0000-0000-000000000000" data-testid="m365-client-id-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Client Secret value</label>
            <Input type="password" value={form.client_secret} onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
              placeholder="Secret value (shown once at creation)" data-testid="m365-client-secret-input" />
            <p className="text-[11px] text-slate-400 mt-1">Stored encrypted at rest. Never shown again after saving.</p>
          </div>
          <Button onClick={connect} disabled={busy} className="w-full" data-testid="m365-connect-submit">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
            {busy ? 'Verifying…' : 'Connect & Verify'}
          </Button>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader>
          <button className="flex items-center justify-between w-full" onClick={() => setShowSteps((s) => !s)} data-testid="m365-steps-toggle">
            <CardTitle className="text-base flex items-center gap-2"><Info className="w-4 h-4" /> How to set up (read-only)</CardTitle>
            {showSteps ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        </CardHeader>
        {showSteps && info && (
          <CardContent className="space-y-4 text-sm">
            <ol className="list-decimal ml-4 space-y-1.5 text-slate-600">
              {info.instructions.map((s, i) => <li key={i}>{s}</li>)}
            </ol>
            <div>
              <p className="text-xs font-medium text-slate-600 mb-1.5">Required application permissions</p>
              <div className="flex flex-wrap gap-1.5">
                {info.required_permissions.map((p) => (
                  <Badge key={p} variant="secondary" className="font-mono text-[11px]">{p}</Badge>
                ))}
              </div>
            </div>
            <p className="text-[12px] text-slate-500 bg-amber-50 border border-amber-200 rounded-lg p-2.5">{info.notes}</p>
            <a href={info.portal_url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-[#0f6cbd] hover:underline" data-testid="m365-portal-link">
              Open Entra admin center <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

/* ---------------- Overview ---------------- */
const OverviewTab = ({ posture, onCollect, collecting, onTab }) => {
  if (!posture?.connected || posture?.score == null) {
    return (
      <Card className="border-slate-200" data-testid="m365-overview-empty">
        <CardContent className="py-12 text-center space-y-3">
          <FileText className="w-10 h-10 text-slate-300 mx-auto" />
          <p className="text-slate-600">No evidence collected yet.</p>
          <Button onClick={onCollect} disabled={collecting} data-testid="m365-overview-collect-btn">
            {collecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
            Collect first snapshot
          </Button>
        </CardContent>
      </Card>
    );
  }
  const s = posture.summary || {};
  const score = posture.score;
  const grade = posture.grade;
  const passed = (posture.findings || []).filter((f) => f.status === 'pass').length;
  const failed = (posture.findings || []).filter((f) => f.status === 'fail' || f.status === 'failed').length;
  const connected = !!posture.connected;
  const lastSyncedAt = posture.collected_at;
  const HealthLine = ({ label, value, ok, testId }) => (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-b-0" data-testid={testId}>
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-sm font-medium text-slate-900 inline-flex items-center gap-1.5">
        {ok && <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />}
        {value}
      </span>
    </div>
  );
  return (
    <div className="space-y-6" data-testid="m365-overview">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Microsoft 365 Health — mirrors Google Workspace's Workspace Health card */}
        <Card className="border-slate-200" data-testid="m365-overview-health">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-700">Microsoft 365 Health</CardTitle></CardHeader>
          <CardContent className="pt-0">
            <HealthLine label="Connection" value={connected ? 'Connected' : 'Not connected'} ok={connected} testId="m365-health-connection" />
            <HealthLine label="Users" value={`${s.total_users ?? 0} synced`} testId="m365-health-users" />
            <HealthLine label="Groups" value={`${s.groups_total ?? 0} synced`} testId="m365-health-groups" />
            <HealthLine label="Domains" value={`${s.domains_total ?? 0} synced`} testId="m365-health-domains" />
            <HealthLine label="Last synced" value={lastSyncedAt ? formatDateTime(parseServerDate(lastSyncedAt)) : '—'} testId="m365-health-last-synced" />
            {(s.skipped || []).length > 0 && (
              <div className="pt-2">
                <Dialog>
                  <DialogTrigger asChild>
                    <button className="text-[11px] text-amber-600 underline underline-offset-2 hover:text-amber-700" data-testid="m365-skipped-note">
                      {s.skipped.length} source(s) skipped — see why
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg" data-testid="m365-skipped-dialog">
                    <DialogHeader>
                      <DialogTitle>Skipped checks</DialogTitle>
                    </DialogHeader>
                    <p className="text-xs text-slate-500">
                      These couldn't be assessed because of a missing permission or license.
                      They are <span className="font-medium">not counted as passed</span> and do not affect your posture score.
                    </p>
                    <div className="space-y-2 mt-2 max-h-[55vh] overflow-y-auto">
                      {s.skipped.map((sk, i) => {
                        const msg = sk.message || sk.reason || '';
                        const isLicense = /p1|p2|licens|premium/i.test(msg);
                        return (
                          <div key={i} className="p-3 rounded-lg border border-slate-100 bg-slate-50/60" data-testid={`m365-skipped-item-${sk.source || i}`}>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-[11px] px-2 py-0.5 rounded-full border ${isLicense ? 'text-purple-700 bg-purple-50 border-purple-200' : 'text-amber-700 bg-amber-50 border-amber-200'}`}>
                                {isLicense ? 'Requires license' : 'Requires permission'}
                              </span>
                              <span className="text-sm font-medium text-slate-800">{SKIPPED_LABELS[sk.source] || (sk.source || '').replace(/_/g, ' ')}</span>
                            </div>
                            <div className="text-xs text-slate-500 mt-1">{msg}</div>
                          </div>
                        );
                      })}
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Security Checks */}
        <Card className="border-slate-200" data-testid="m365-overview-checks">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-700">Security Checks</CardTitle></CardHeader>
          <CardContent className="pt-0">
            <div className="flex items-center gap-4 mb-3">
              <span className="inline-flex items-center gap-1.5 text-emerald-700 font-medium" data-testid="m365-checks-passed"><CheckCircle className="w-4 h-4" /> Passed {passed}</span>
              <span className="inline-flex items-center gap-1.5 text-red-700 font-medium" data-testid="m365-checks-failed"><XCircle className="w-4 h-4" /> Failed {failed}</span>
            </div>
            <Button variant="outline" size="sm" onClick={() => onTab('checks')} data-testid="m365-view-checks-btn">View checks</Button>
          </CardContent>
        </Card>

        {/* Security Posture */}
        <Card className="border-slate-200" data-testid="m365-overview-posture">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-700">Security Posture</CardTitle></CardHeader>
          <CardContent className="pt-0">
            <div className={`text-5xl font-bold ${scoreTone(score)}`} data-testid="m365-posture-score">{score == null ? '—' : `${score}%`}</div>
            <div className="text-sm text-slate-500 mt-2">Grade {grade || '—'}</div>
          </CardContent>
        </Card>
      </div>

      {(() => {
        // Build directly from FAILING checks so the worst failures always
        // surface (e.g. a failing "Baseline identity protection"). Exclude
        // passes and not-evaluated (license/permission-gated) items. Points are
        // weighted by the check's real severity; sorted worst-first.
        const isFail = (f) => f.status === 'fail' || f.status === 'failed';
        const fixes = (posture.findings || [])
          .filter(isFail)
          .slice()
          .sort((a, b) => sevRank(a.severity) - sevRank(b.severity)
            || sevPoints(b.severity) - sevPoints(a.severity))
          .slice(0, 8);
        if (!fixes.length) return null;
        return (
          <Card className="border-slate-200" data-testid="m365-top-fixes">
            <CardHeader><CardTitle className="text-base">Priority fixes</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {fixes.map((f) => (
                <div key={f.rule_key} className="flex items-start justify-between gap-3 p-3 rounded-lg border border-slate-100 bg-slate-50/60" data-testid={`m365-fix-${f.rule_key}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[11px] px-2 py-0.5 rounded-full border ${SEV_TONE[f.severity] || SEV_TONE.low}`} data-testid={`m365-fix-severity-${f.rule_key}`}>{sevLabel(f.severity)}</span>
                      <span className="text-sm font-medium text-slate-800">{f.label || f.rule_key}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{f.what_would_fix || f.proof}</div>
                    {(f.frameworks || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`m365-fix-frameworks-${f.rule_key}`}>
                        {f.frameworks.map((fw) => (
                          <span key={fw} className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 bg-white text-slate-600">{fw}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <Badge variant="outline" className="shrink-0" data-testid={`m365-fix-points-${f.rule_key}`}>+{sevPoints(f.severity)} pts</Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        );
      })()}
    </div>
  );
};

/* ---------------- Checks ---------------- */
const ChecksTab = ({ posture, onEvaluate, isEvaluating }) => {
  const findings = posture?.findings || [];
  // Reuse the shared Google Workspace checks table (display-only), adapted to
  // Microsoft 365 field names — same table UI, no new component.
  return (
    <div className="space-y-6" data-testid="m365-checks">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-lg font-semibold text-slate-900">Security Checks</h2>
        <PostureFreshnessPill
          posture={posture}
          isEvaluating={isEvaluating}
          onEvaluate={onEvaluate}
          testIdPrefix="m365"
        />
      </div>

      {!findings.length ? (
        <Card className="border-slate-200"><CardContent className="py-10 text-center text-slate-500" data-testid="m365-checks-empty">No checks available. Collect evidence first.</CardContent></Card>
      ) : (
        <SecurityChecksTable
          posture={posture}
          testIdPrefix="m365"
          openLabel="Open in Entra"
          passedSeverityLabel={null}
          getOpenUrl={(f) => f.deep_link}
          getFixShort={(f) => m365FixLocation(f)}
          getWhatWeFound={(f) => f.proof}
          getHowToFix={(f) => f.remediation}
          getWhere={() => null}
          getNaFixShort={(f) => m365NaFix(f)}
          getNaHowToFix={(f) => m365NaHowToFix(f)}
          getFrameworksSatisfied={(f) => toParentFrameworks([...(f.frameworks || []), ...(f.control_ids || [])])}
        />
      )}
    </div>
  );
};

/* ---------------- Directory ---------------- */
const DirectoryTab = ({ directory, settings }) => {
  const [sub, setSub] = useState('users');
  const [openRoleGroups, setOpenRoleGroups] = useState({});
  if (!directory?.connected) return <Card className="border-slate-200"><CardContent className="py-10 text-center text-slate-500" data-testid="m365-directory-empty">No directory data yet. Collect evidence first.</CardContent></Card>;
  const subTabs = [
    ['users', `Users (${(directory.users || []).length})`],
    ['groups', `Groups (${(directory.groups || []).length})`],
    ['roles', `Admin roles (${(directory.roles || []).length})`],
    ['units', `Admin units (${(directory.administrative_units || []).length})`],
    ['domains', `Domains (${(directory.domains || []).length})`],
  ];
  return (
    <div className="space-y-4" data-testid="m365-directory">
      <div className="flex flex-wrap gap-1.5">
        {subTabs.map(([id, label]) => (
          <button key={id} onClick={() => setSub(id)} data-testid={`m365-dir-sub-${id}`}
            className={`px-3 py-1.5 text-xs font-medium rounded-full border ${sub === id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>{label}</button>
        ))}
      </div>

      {sub === 'users' && (
        <Card className="border-slate-200 overflow-hidden">
          <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-100 text-xs" data-testid="m365-users-summary">
            <span className="text-slate-600">{(directory.users || []).length} accounts</span>
            <Badge className="bg-amber-100 text-amber-800 border-amber-200" data-testid="m365-users-guest-count">{(directory.users || []).filter((u) => (u.user_type || '').toLowerCase() === 'guest').length} guest / external</Badge>
            <Badge variant="secondary" className="text-slate-500" data-testid="m365-users-disabled-count">{(directory.users || []).filter((u) => !u.enabled).length} disabled</Badge>
          </div>
          <table className="w-full text-sm" data-testid="m365-users-table">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr><th className="text-left px-4 py-2.5">Name</th><th className="text-left px-4 py-2.5">Email</th><th className="text-left px-4 py-2.5">Type</th><th className="text-left px-4 py-2.5">Status</th><th className="text-left px-4 py-2.5">MFA</th><th className="text-left px-4 py-2.5">Last sign-in</th></tr>
            </thead>
            <tbody>
              {(directory.users || []).map((u) => {
                const isGuest = (u.user_type || '').toLowerCase() === 'guest';
                return (
                <tr key={u.id} className={`border-t border-slate-100 ${!u.enabled ? 'bg-slate-50/60' : ''}`}>
                  <td className="px-4 py-2.5 text-slate-800">{u.display_name || '—'}{u.is_admin && <Badge className="ml-2 text-[10px]" variant="secondary">admin</Badge>}</td>
                  <td className="px-4 py-2.5 text-slate-600">{u.email || '—'}</td>
                  <td className="px-4 py-2.5">{isGuest ? <Badge className="text-[10px] bg-amber-100 text-amber-800 border-amber-200" data-testid={`m365-user-guest-${u.id}`}>Guest</Badge> : <span className="text-slate-600">{u.user_type || 'Member'}</span>}</td>
                  <td className="px-4 py-2.5">{u.enabled ? <span className="text-emerald-700">Enabled</span> : <Badge className="text-[10px] bg-slate-200 text-slate-600 border-slate-300" data-testid={`m365-user-disabled-${u.id}`}>Disabled</Badge>}</td>
                  <td className="px-4 py-2.5">{u.mfa_registered === true ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : u.mfa_registered === false ? <XCircle className="w-4 h-4 text-slate-300" /> : <span className="text-slate-400" title="MFA data not available">—</span>}</td>
                  <td className="px-4 py-2.5 text-slate-500">{u.last_sign_in ? formatDateTime(parseServerDate(u.last_sign_in)) : '—'}</td>
                </tr>
              );})}
            </tbody>
          </table>
        </Card>
      )}

      {sub === 'groups' && (
        <Card className="border-slate-200 overflow-hidden">
          <table className="w-full text-sm" data-testid="m365-groups-table">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="text-left px-4 py-2.5">Name</th><th className="text-left px-4 py-2.5">Email</th><th className="text-left px-4 py-2.5">Type</th><th className="text-left px-4 py-2.5">Members</th></tr></thead>
            <tbody>
              {(directory.groups || []).map((g) => (
                <tr key={g.id} className="border-t border-slate-100">
                  <td className="px-4 py-2.5 text-slate-800">{g.name || '—'}{g.role_assignable && <Badge className="ml-2 text-[10px] bg-amber-100 text-amber-800 border-amber-200" data-testid={`m365-group-roleassignable-${g.id}`}>Role-assignable</Badge>}</td>
                  <td className="px-4 py-2.5 text-slate-600">{g.email || '—'}</td>
                  <td className="px-4 py-2.5"><Badge variant="secondary" className="text-[11px]" data-testid={`m365-group-type-${g.id}`}>{g.group_type || 'Other'}</Badge></td>
                  <td className="px-4 py-2.5 text-slate-600">{g.member_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {sub === 'roles' && (() => {
        const roles = directory.roles || [];
        // Per-user role-assignment counts → over-privilege banner.
        const counts = {};
        roles.forEach((r) => (r.members || []).forEach((m) => {
          const k = m.email || m.id; if (!k) return;
          counts[k] = counts[k] || { count: 0, name: m.name || m.email };
          counts[k].count += 1;
        }));
        const THRESHOLD = Number(settings?.m365_admin_roles_per_user_cap ?? 10);
        const flagged = Object.values(counts).filter((x) => x.count > THRESHOLD).sort((a, b) => b.count - a.count);
        // Group non-GA roles that share the exact same member set.
        const ga = roles.filter((r) => r.is_global_admin);
        const others = roles.filter((r) => !r.is_global_admin);
        const gmap = new Map();
        others.forEach((r) => {
          const key = (r.members || []).map((m) => m.email || m.id).sort().join('|') || '(none)';
          if (!gmap.has(key)) gmap.set(key, { key, members: r.members || [], roles: [] });
          gmap.get(key).roles.push(r);
        });
        const groups = [...gmap.values()].sort((a, b) => b.roles.length - a.roles.length);
        const memberBadges = (members, ga2) => members.map((m) => (
          <Badge key={m.id} variant="secondary" className={`text-[11px] ${ga2 ? 'bg-amber-100 text-amber-900' : ''}`}>
            {m.name ? `${m.name}${m.email ? ` · ${m.email}` : ''}` : (m.email || m.id)}
          </Badge>
        ));
        const roleCard = (r) => (
          <Card key={r.id} className={`${r.is_global_admin ? 'border-amber-300 bg-amber-50/60' : 'border-slate-200'}`} data-testid={`m365-role-${r.id}`}>
            <CardContent className="py-4">
              <div className="text-sm font-medium text-slate-800 mb-2 flex items-center gap-2 flex-wrap">
                {r.is_global_admin && <Shield className="w-4 h-4 text-amber-600" />}
                {r.name}
                {r.is_global_admin && <Badge className="text-[10px] bg-amber-100 text-amber-800 border-amber-200" data-testid={`m365-role-ga-badge-${r.id}`}>Global admin · full control</Badge>}
                <span className="text-slate-400 font-normal">· {r.members.length}</span>
              </div>
              <div className="flex flex-wrap gap-1.5">{memberBadges(r.members, r.is_global_admin)}</div>
            </CardContent>
          </Card>
        );
        return (
          <div className="space-y-3" data-testid="m365-roles-list">
            {directory.roles_error && !roles.length && (
              <Card className="border-red-200 bg-red-50" data-testid="m365-roles-error">
                <CardContent className="py-4 text-sm text-red-800">
                  <div className="font-medium">Couldn't read directory role assignments</div>
                  <div className="mt-1 text-red-700 text-xs">Microsoft Graph returned {directory.roles_error}. Grant <span className="font-mono">RoleManagement.Read.Directory</span> (or Directory.Read.All) admin consent in Entra, click Verify now, then reload.</div>
                </CardContent>
              </Card>
            )}

            {flagged.length > 0 && (
              <Card className="border-amber-300 bg-amber-50" data-testid="m365-roles-overprivilege-banner">
                <CardContent className="py-3 flex items-start gap-2 text-sm text-amber-900">
                  <AlertCircle className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" />
                  <div>
                    <span className="font-semibold">⚠ {flagged.length} user{flagged.length > 1 ? 's' : ''} hold more than {THRESHOLD} admin role assignments each</span> — review for least privilege.
                    <div className="text-xs text-amber-800 mt-0.5">{flagged.slice(0, 4).map((x) => `${x.name} (${x.count})`).join(' · ')}</div>
                  </div>
                </CardContent>
              </Card>
            )}

            {ga.map(roleCard)}

            {groups.map((grp) => {
              if (grp.roles.length === 1) return roleCard(grp.roles[0]);
              const open = !!openRoleGroups[grp.key];
              return (
                <Card key={grp.key} className="border-slate-200" data-testid={`m365-role-group-${grp.key}`}>
                  <button type="button" onClick={() => setOpenRoleGroups((s) => ({ ...s, [grp.key]: !s[grp.key] }))}
                    className="w-full text-left hover:bg-slate-50 transition-colors" data-testid={`m365-role-group-toggle-${grp.key}`}>
                    <CardContent className="py-4 flex items-center gap-2 flex-wrap">
                      {open ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
                      <span className="text-sm font-semibold text-slate-800">{grp.roles.length} roles</span>
                      <span className="text-slate-300">·</span>
                      <div className="flex flex-wrap gap-1.5">{memberBadges(grp.members, false)}</div>
                    </CardContent>
                  </button>
                  {open && (
                    <div className="px-4 pb-4 -mt-1 flex flex-wrap gap-1.5" data-testid={`m365-role-group-list-${grp.key}`}>
                      {grp.roles.map((r) => <Badge key={r.id} variant="outline" className="text-[11px] text-slate-600">{r.name}</Badge>)}
                    </div>
                  )}
                </Card>
              );
            })}

            {!roles.length && !directory.roles_error && (
              <p className="text-sm text-slate-500" data-testid="m365-roles-empty">No directory role assignments found.</p>
            )}
          </div>
        );
      })()}

      {sub === 'units' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(directory.administrative_units || []).map((a) => (
            <Card key={a.id} className="border-slate-200"><CardContent className="py-4"><div className="text-sm font-medium text-slate-800">{a.name}</div>{a.description && <div className="text-xs text-slate-500 mt-1">{a.description}</div>}</CardContent></Card>
          ))}
          {!(directory.administrative_units || []).length && (
            directory.units_error
              ? <Card className="border-red-200 bg-red-50" data-testid="m365-units-error"><CardContent className="py-4 text-sm text-red-800"><div className="font-medium">Couldn't read administrative units</div><div className="mt-1 text-xs text-red-700">Microsoft Graph returned {directory.units_error}.</div></CardContent></Card>
              : <p className="text-sm text-slate-500" data-testid="m365-units-empty">None — requires Entra ID P1</p>
          )}
        </div>
      )}

      {sub === 'domains' && (
        <div className="space-y-2" data-testid="m365-domains-list">
          {(directory.domains || []).map((d) => (
            <Card key={d.name} className="border-slate-200" data-testid={`m365-domain-${d.name}`}>
              <CardContent className="py-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm font-medium text-slate-800">{d.name}</div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {d.is_default && <Badge className="text-[10px] bg-blue-100 text-blue-800 border-blue-200">Default</Badge>}
                  <Badge className={`text-[10px] ${d.is_verified ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-amber-100 text-amber-800 border-amber-200'}`}>{d.is_verified ? 'Verified' : 'Unverified'}</Badge>
                  {d.auth_type && <Badge variant="secondary" className="text-[10px]" data-testid={`m365-domain-auth-${d.name}`}>{d.auth_type}</Badge>}
                </div>
              </CardContent>
            </Card>
          ))}
          {!(directory.domains || []).length && <p className="text-sm text-slate-500">No domains found.</p>}
        </div>
      )}
    </div>
  );
};

/* ---------------- Audit Evidence log ---------------- */
const friendlyEvidenceType = (ev) => {
  if (ev.evidence_type === 'microsoft365_control_evidence') return ev.control_title || 'Control Evidence';
  const key = (ev.evidence_type || '').toLowerCase();
  if (key.includes('directory')) return 'Directory Snapshot';
  if (key.includes('posture')) return 'Posture Evaluation';
  return (ev.evidence_type || 'Evidence').replace(/microsoft365_/g, '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const ReadableList = ({ rows }) => {
  const cols = [...new Set(rows.flatMap((r) => (r && typeof r === 'object' && !Array.isArray(r)) ? Object.keys(r) : []))].slice(0, 6);
  if (!cols.length) {
    return <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700">{rows.slice(0, 200).map((r, i) => <li key={i}>{typeof r === 'object' ? JSON.stringify(r) : String(r)}</li>)}</ul>;
  }
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-md">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>{cols.map((c) => <th key={c} className="text-left px-3 py-2 font-medium">{c.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}</th>)}</tr></thead>
        <tbody className="divide-y divide-slate-100">
          {rows.slice(0, 200).map((r, i) => <tr key={i}>{cols.map((c) => <td key={c} className="px-3 py-2 text-slate-700">{r && typeof r[c] === 'object' ? JSON.stringify(r[c]) : (r ? String(r[c] ?? '—') : '—')}</td>)}</tr>)}
        </tbody>
      </table>
    </div>
  );
};

const NOVA_FRAMEWORKS = ['SOC 2', 'GDPR', 'ISO 27001', 'HIPAA', 'PCI DSS'];
const toParentFrameworks = (list) => {
  const found = new Set();
  (list || []).forEach((raw) => {
    if (!raw) return;
    const s = String(raw).toUpperCase().replace(/\s+/g, ' ').trim();
    NOVA_FRAMEWORKS.forEach((fw) => { if (s.startsWith(fw.toUpperCase())) found.add(fw); });
  });
  return NOVA_FRAMEWORKS.filter((fw) => found.has(fw));
};

const EvidenceViewerModal = ({ evidence, workspaceId, onClose }) => {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRaw, setShowRaw] = useState(false);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`/api/technologies/evidence/${evidence._id}`, { params: { workspace_id: workspaceId } });
        setDoc(r.data);
      } catch { setDoc(evidence); } finally { setLoading(false); }
    })();
  }, [evidence, workspaceId]);

  const d = doc || evidence;
  const ce = d?.data?.raw_data?.control_evidence;
  const ms = d?.data?.raw_data?.microsoft365;
  const name = ce?.label || d.control_title || friendlyEvidenceType(evidence);
  const result = d.result || ce?.result || 'collected';
  const collectedOn = d.last_collected_at || d.collected_at || d.collection_timestamp;
  const frameworks = d.frameworks || ce?.frameworks || [];
  const controlIds = d.control_ids || ce?.control_ids || [];
  const parentFrameworks = toParentFrameworks([...frameworks, ...controlIds]);
  const proof = ce?.proof;
  const remediation = ce?.remediation;
  const matches = Array.isArray(ce?.matches) ? ce.matches : [];
  const dirCounts = ms ? {
    Users: (ms.users || []).length, Groups: (ms.groups || []).length,
    'Directory roles': (ms.directory_roles || []).length, Domains: (ms.domains || []).length,
    'Administrative units': (ms.administrative_units || []).length,
  } : null;
  const dirDomains = ms && Array.isArray(ms.domains) ? ms.domains.map((x) => ({ domain: x.id || x.name, default: !!x.isDefault, verified: !!x.isVerified, authentication: x.authenticationType })) : [];

  const resultStyle = result === 'pass' ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
    : result === 'fail' ? 'bg-red-100 text-red-800 border-red-200'
    : 'bg-slate-100 text-slate-700 border-slate-200';
  const resultLabel = result === 'pass' ? 'Pass' : result === 'fail' ? 'Fail' : 'Collected';

  const download = () => {
    const blob = new Blob([JSON.stringify(d, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(name || 'evidence').replace(/\s+/g, '_').toLowerCase()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col overflow-hidden" data-testid="m365-evidence-viewer">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3 pr-6">
            <span className="truncate">{name}</span>
            <Button size="sm" variant="outline" onClick={download} data-testid="m365-evidence-download-json"><Download className="w-4 h-4 mr-2" /> Download JSON</Button>
          </DialogTitle>
        </DialogHeader>
        {loading ? <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div> : (
          <div className="overflow-auto space-y-5 pr-1" style={{ maxHeight: '68vh' }} data-testid="m365-evidence-readable">
            <div className="flex flex-wrap items-center gap-3">
              <Badge className={`text-xs border ${resultStyle}`} data-testid="m365-evidence-result">{result === 'fail' ? '✗' : '✓'} {resultLabel}</Badge>
              {collectedOn && <span className="text-xs text-slate-500">Collected on {formatDateTime(parseServerDate(collectedOn))}</span>}
            </div>

            {parentFrameworks.length > 0 && (
              <div>
                <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Frameworks satisfied</div>
                <div className="flex flex-wrap gap-1.5">
                  {parentFrameworks.map((fw) => <Badge key={fw} variant="secondary" className="text-[11px]">{fw}</Badge>)}
                </div>
              </div>
            )}

            {proof && (
              <div>
                <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">What we found</div>
                <p className="text-sm text-slate-800 leading-relaxed">{proof}</p>
              </div>
            )}

            {remediation && result === 'fail' && (
              <div>
                <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">How to fix</div>
                <p className="text-sm text-slate-700 leading-relaxed">{remediation}</p>
              </div>
            )}

            {dirCounts && (
              <div>
                <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Snapshot summary</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {Object.entries(dirCounts).map(([k, v]) => (
                    <div key={k} className="rounded-md border border-slate-200 px-3 py-2"><div className="text-lg font-semibold text-slate-900">{v}</div><div className="text-xs text-slate-500">{k}</div></div>
                  ))}
                </div>
              </div>
            )}

            {matches.length > 0 && (
              <div>
                <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Details ({matches.length})</div>
                <ReadableList rows={matches} />
              </div>
            )}

            {dirDomains.length > 0 && (
              <div>
                <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Domains ({dirDomains.length})</div>
                <ReadableList rows={dirDomains} />
              </div>
            )}

            <div className="border-t border-slate-100 pt-3">
              <button type="button" onClick={() => setShowRaw((s) => !s)} className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900" data-testid="m365-evidence-raw-toggle">
                {showRaw ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />} View raw JSON
              </button>
              {showRaw && (
                <div className="mt-2 overflow-auto rounded-md bg-slate-900 text-slate-100 text-xs p-4 font-mono" style={{ maxHeight: '40vh' }}>
                  <pre className="whitespace-pre-wrap break-words" data-testid="m365-evidence-json">{JSON.stringify(d?.data ?? d, null, 2)}</pre>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const EvidenceLogTab = ({ workspaceId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRaw, setShowRaw] = useState(false);
  const [active, setActive] = useState(null);
  const user = useMemo(() => JSON.parse(localStorage.getItem('user') || 'null'), []);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`/api/technologies/evidence-history/${user?.id || 'any'}/${workspaceId}`,
        { params: { technology: 'microsoft_365', include_data: false } });
      setRows(r.data?.history || r.data?.evidence || []);
    } catch { /* noop */ } finally { setLoading(false); }
  }, [user, workspaceId]);
  useEffect(() => { load(); }, [load]);

  const when = (ev) => ev.last_collected_at || ev.collected_at || ev.collection_timestamp;
  const fmt = (ev) => { const d = parseServerDate(when(ev)); return d ? formatDateTime(d) : '—'; };

  return (
    <Card className="border-slate-200" data-testid="m365-evidence-log">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4 text-slate-500" /> Audit Evidence</CardTitle>
          <div className="flex items-center gap-3">
            <label className="inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer" data-testid="m365-evidence-show-raw-toggle">
              <input type="checkbox" checked={showRaw} onChange={(e) => setShowRaw(e.target.checked)} className="rounded border-slate-300" />
              Show Raw Evidence
            </label>
            <Button onClick={load} variant="ghost" size="sm" disabled={loading} data-testid="m365-evidence-refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading && rows.length === 0 && <div className="py-6 text-center text-sm text-slate-500"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />Loading evidence…</div>}
        {!loading && rows.length === 0 && <div className="py-8 text-center text-sm text-slate-500" data-testid="m365-evidence-empty"><FileText className="w-7 h-7 mx-auto text-slate-300 mb-2" />No audit evidence collected yet.</div>}
        {rows.length > 0 && (
          <div className="overflow-x-auto border border-slate-200 rounded-md" data-testid="m365-evidence-list">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">Date</th>
                  <th className="text-left px-4 py-2.5 font-medium">Evidence Type</th>
                  <th className="text-left px-4 py-2.5 font-medium">Status</th>
                  {showRaw && <th className="text-left px-4 py-2.5 font-medium">Raw</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.slice(0, 60).map((ev, i) => {
                  const isControl = ev.evidence_type === 'microsoft365_control_evidence';
                  const pass = ev.result === 'pass';
                  return (
                    <tr key={ev._id || i} className="hover:bg-slate-50 cursor-pointer" onClick={() => setActive(ev)} data-testid={`m365-evidence-row-${i}`}>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmt(ev)}</td>
                      <td className="px-4 py-3 font-medium text-slate-900">{friendlyEvidenceType(ev)}</td>
                      <td className="px-4 py-3">
                        {isControl
                          ? <span className={`inline-flex items-center gap-1 text-xs font-medium ${pass ? 'text-emerald-700' : 'text-red-700'}`}>{pass ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}{pass ? 'Pass' : 'Fail'}</span>
                          : <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700"><CheckCircle className="w-3.5 h-3.5" />Success</span>}
                      </td>
                      {showRaw && <td className="px-4 py-3 text-[11px] font-mono text-slate-500">{ev.evidence_type}{ev.rule_key ? ` · ${ev.rule_key}` : ''}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      {active && <EvidenceViewerModal evidence={active} workspaceId={workspaceId} onClose={() => setActive(null)} />}
    </Card>
  );
};

/* ---------------- Settings ---------------- */
const PERM_STATE = {
  granted: { label: 'Granted', cls: 'text-emerald-700', Icon: CheckCircle },
  missing: { label: 'Missing', cls: 'text-red-700', Icon: XCircle },
  license: { label: 'License-gated', cls: 'text-amber-700', Icon: AlertCircle },
  error: { label: 'Error', cls: 'text-red-700', Icon: XCircle },
  unknown: { label: 'Not collected', cls: 'text-slate-400', Icon: Info },
};

const M365SettingsInfoCards = ({ workspaceId }) => {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      if (!workspaceId) { setLoading(false); return; }
      try {
        const r = await axios.get(`/api/technologies/microsoft-365/settings-info/${workspaceId}`);
        setInfo(r.data);
      } catch { setInfo(null); } finally { setLoading(false); }
    })();
  }, [workspaceId]);

  if (loading) {
    return (
      <Card className="border-slate-200" data-testid="m365-settings-info-loading">
        <CardContent className="py-6 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading permissions & license…</CardContent>
      </Card>
    );
  }
  if (!info || !info.connected) {
    return (
      <Card className="border-slate-200" data-testid="m365-settings-info-empty">
        <CardHeader className="pb-3"><CardTitle className="text-base">Permissions & Consent</CardTitle></CardHeader>
        <CardContent className="py-2 text-sm text-slate-500">
          Run <span className="font-medium text-slate-700">Collect Evidence</span> to populate permission, license and collection status.
        </CardContent>
      </Card>
    );
  }

  const perms = info.permissions || [];
  const lic = info.license || {};
  const sch = info.schedule || {};
  const grantedCount = perms.filter((p) => p.state === 'granted').length;
  const licTone = lic.tier === 'P2' ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
    : lic.tier === 'P1' ? 'bg-amber-100 text-amber-800 border-amber-200'
    : lic.tier === 'Free' ? 'bg-red-100 text-red-800 border-red-200'
    : 'bg-slate-100 text-slate-700 border-slate-200';

  return (
    <>
      {/* Permissions & Consent */}
      <Card className="border-slate-200" data-testid="m365-permissions-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            Permissions & Consent
            <Badge variant="secondary" className="text-[11px]">{grantedCount}/{perms.length} granted</Badge>
          </CardTitle>
          <CardDescription className="text-xs">
            Microsoft Graph application permissions Nova needs, based on the last collection.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2.5">
          {perms.map((p) => {
            const s = PERM_STATE[p.state] || PERM_STATE.unknown;
            const Icon = s.Icon;
            return (
              <div key={p.permission} className="flex items-start justify-between gap-4 border-b border-slate-50 pb-2.5 last:border-0 last:pb-0" data-testid={`m365-perm-${p.permission}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-mono text-slate-800">{p.permission}</div>
                  <div className="text-[11px] text-slate-500">{p.label}</div>
                  {p.hint && <div className="text-[11px] text-amber-700 mt-0.5">{p.hint}</div>}
                </div>
                <span className={`inline-flex items-center gap-1 text-xs font-medium whitespace-nowrap ${s.cls}`} data-testid={`m365-perm-state-${p.permission}`}>
                  <Icon className="w-3.5 h-3.5" />{s.label}
                </span>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Entra License Tier */}
      <Card className="border-slate-200" data-testid="m365-license-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-slate-500" /> Entra ID License
            <Badge className={`text-[11px] border ${licTone}`} data-testid="m365-license-tier">{lic.tier}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p className={`leading-relaxed ${lic.tier === 'Free' || lic.tier === 'P1' ? 'text-amber-800' : 'text-slate-600'}`} data-testid="m365-license-note">{lic.note}</p>
          {(lic.skus || []).length > 0 && (
            <div className="flex flex-wrap gap-1 pt-1">
              {lic.skus.map((s) => <Badge key={s} variant="outline" className="text-[10px] text-slate-500">{s}</Badge>)}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Collection Schedule */}
      <Card className="border-slate-200" data-testid="m365-schedule-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4 text-slate-500" /> Collection Schedule</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex items-center justify-between py-1.5 border-b border-slate-50">
            <span className="text-slate-500">Cadence</span>
            <span className="text-slate-800 font-medium" data-testid="m365-schedule-cadence">{sch.cadence}</span>
          </div>
          <div className="flex items-center justify-between py-1.5 border-b border-slate-50">
            <span className="text-slate-500">Last collected</span>
            <span className="text-slate-800" data-testid="m365-schedule-last">{sch.last_collected ? formatDateTime(parseServerDate(sch.last_collected)) : '—'}</span>
          </div>
          <p className="text-[11px] text-slate-500 pt-1">{sch.note}</p>
        </CardContent>
      </Card>
    </>
  );
};

const M365_THRESHOLD_DEFAULTS = {
  m365_inactive_account_threshold_days: 90,
  m365_never_logged_in_grace_days: 14,
  m365_global_admin_cap: 4,
  m365_admin_roles_per_user_cap: 10,
};

const SecurityThresholdsCard = ({ workspaceId, onSettingsSaved }) => {
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = workspaceId
        ? await axios.get('/api/workspace-settings/microsoft-365', { params: { workspace_id: workspaceId } })
        : { data: {} };
      const merged = { ...M365_THRESHOLD_DEFAULTS, ...(r.data || {}) };
      setSettings(merged);
      setDraft({
        m365_inactive_account_threshold_days: merged.m365_inactive_account_threshold_days ?? 90,
        m365_never_logged_in_grace_days: merged.m365_never_logged_in_grace_days ?? 14,
        m365_global_admin_cap: merged.m365_global_admin_cap ?? 4,
        m365_admin_roles_per_user_cap: merged.m365_admin_roles_per_user_cap ?? 10,
      });
    } catch {
      // Never leave the card empty — fall back to defaults so the fields render.
      setSettings({ ...M365_THRESHOLD_DEFAULTS });
      setDraft({ ...M365_THRESHOLD_DEFAULTS });
    } finally { setLoading(false); }
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  const dirty = useMemo(() => {
    if (!settings) return false;
    return Object.keys(M365_THRESHOLD_DEFAULTS).some(
      (k) => draft[k] !== (settings[k] ?? M365_THRESHOLD_DEFAULTS[k]));
  }, [draft, settings]);

  const onSave = async () => {
    try {
      setSaving(true);
      const r = await axios.put('/api/workspace-settings/microsoft-365', draft, { params: { workspace_id: workspaceId } });
      setSettings(r.data);
      toast.success('Settings saved');
      onSettingsSaved && onSettingsSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const onReset = () => {
    if (!settings) return;
    setDraft({
      m365_inactive_account_threshold_days: settings.m365_inactive_account_threshold_days ?? 90,
      m365_never_logged_in_grace_days: settings.m365_never_logged_in_grace_days ?? 14,
      m365_global_admin_cap: settings.m365_global_admin_cap ?? 4,
      m365_admin_roles_per_user_cap: settings.m365_admin_roles_per_user_cap ?? 10,
    });
  };

  return (
    <Card className="border-slate-200" data-testid="m365-security-thresholds">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Security Thresholds</CardTitle>
        <CardDescription className="text-xs">
          These limits drive your security checks. Changes apply on the next evaluation.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && !settings && (
          <div className="text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
        )}
        {settings && (
          <>
            <M365SettingField
              label="Inactive account threshold (days)"
              helper="Mark a user as inactive if last sign-in is older than this many days."
              value={draft.m365_inactive_account_threshold_days}
              min={7} max={365}
              onChange={(v) => setDraft((d) => ({ ...d, m365_inactive_account_threshold_days: v }))}
              testId="m365-set-inactive-days"
            />
            <M365SettingField
              label="Never-logged-in grace (days)"
              helper="Allow a freshly created account this many days before flagging it as inactive."
              value={draft.m365_never_logged_in_grace_days}
              min={0} max={90}
              onChange={(v) => setDraft((d) => ({ ...d, m365_never_logged_in_grace_days: v }))}
              testId="m365-set-grace-days"
            />
            <M365SettingField
              label="Global Admin cap"
              helper="Posture is penalised when the number of Global Administrators exceeds this cap."
              value={draft.m365_global_admin_cap}
              min={1} max={25}
              onChange={(v) => setDraft((d) => ({ ...d, m365_global_admin_cap: v }))}
              testId="m365-set-global-admin-cap"
            />
            <M365SettingField
              label="Admin-role-per-user cap"
              helper="Flag any user holding more admin roles than this in the Directory over-privilege warning."
              value={draft.m365_admin_roles_per_user_cap}
              min={1} max={100}
              onChange={(v) => setDraft((d) => ({ ...d, m365_admin_roles_per_user_cap: v }))}
              testId="m365-set-admin-roles-cap"
            />

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
              <Button variant="ghost" size="sm" onClick={onReset} disabled={!dirty || saving} data-testid="m365-settings-reset-btn">
                Reset
              </Button>
              <Button size="sm" onClick={onSave} disabled={!dirty || saving} data-testid="m365-settings-save-btn">
                <Save className="w-3.5 h-3.5 mr-1" />{saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const M365SettingField = ({ label, helper, value, min, max, onChange, testId }) => (
  <div className="flex items-start justify-between gap-4">
    <div className="min-w-0 flex-1">
      <label className="text-sm font-medium text-slate-800">{label}</label>
      {helper && <p className="text-[11px] text-slate-500 mt-0.5">{helper}</p>}
    </div>
    <Input
      type="number" min={min} max={max} value={value}
      onChange={(e) => {
        const v = parseInt(e.target.value, 10);
        if (Number.isNaN(v)) return;
        onChange(Math.min(max, Math.max(min, v)));
      }}
      className="w-24 h-8 text-sm text-right"
      data-testid={testId}
    />
  </div>
);

const SettingsTab = ({ connection, user, workspaceId, onChanged, onSettingsSaved, onDisconnected }) => {
  const [verifying, setVerifying] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const verify = async () => {
    setVerifying(true);
    try {
      const r = await axios.post('/api/technologies/microsoft-365/verify', { user_id: user.id, workspace_id: workspaceId });
      r.data?.success ? toast.success('Connection verified') : toast.error(r.data?.last_verification?.error_msg || 'Verification failed');
      onChanged();
    } catch (e) { toast.error(errMsg(e, 'Verify failed')); } finally { setVerifying(false); }
  };
  const disconnect = async () => {
    if (!window.confirm('Disconnect Microsoft 365? Collected evidence is retained.')) return;
    setDisconnecting(true);
    try {
      await axios.post('/api/technologies/disconnect', { user_id: user.id, workspace_id: workspaceId, technology: 'microsoft_365' });
      toast.success('Disconnected');
      onDisconnected ? onDisconnected() : onChanged();
    } catch (e) { toast.error(errMsg(e, 'Disconnect failed')); } finally { setDisconnecting(false); }
  };
  return (
    <div className="space-y-4" data-testid="m365-settings">
      <SecurityThresholdsCard workspaceId={workspaceId} onSettingsSaved={onSettingsSaved} />
      <M365SettingsInfoCards workspaceId={workspaceId} />
      <Card className="border-slate-200">
        <CardHeader><CardTitle className="text-base">Connection</CardTitle></CardHeader>
        <CardContent className="space-y-2 text-sm">
          <Row label="Tenant" value={connection?.connection_params?.tenant_name || connection?.credentials?.tenant_id} />
          <Row label="Tenant ID" value={connection?.credentials?.tenant_id} mono />
          <Row label="Client ID" value={connection?.credentials?.client_id} mono />
          <Row label="Status" value={connection?.status} />
          <Row label="Last verified" value={connection?.last_verification?.at ? formatDateTime(parseServerDate(connection.last_verification.at)) : '—'} />
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={verify} disabled={verifying} data-testid="m365-verify-btn">
              {verifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />} Verify now
            </Button>
            <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={disconnect} disabled={disconnecting} data-testid="m365-disconnect-btn">
              {disconnecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Disconnect
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const Row = ({ label, value, mono }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
    <span className="text-slate-500">{label}</span>
    <span className={`text-slate-800 ${mono ? 'font-mono text-xs' : ''}`}>{value || '—'}</span>
  </div>
);

export default Microsoft365Detail;
